import React from "react";
import "./FaqPage.css";

type FaqSection = {
  id: string;
  title: string;
  description: string;
  roles: string[];
  steps: string[];
  tips?: string[];
};

const faqSections: FaqSection[] = [
  {
    id: "dashboard",
    title: "Dashboard",
    description: "Use the dashboard to get a fast picture of who is available, who is resting, and where rule problems are starting to show up.",
    roles: ["All roles"],
    steps: [
      "Open Dashboard to see your high-level counts and the main activity panels.",
      "Review Unavailable Pitchers to see who is resting and the next date they can pitch.",
      "Review Available Pitchers when you need a fast list of ready arms.",
      "Use Rule Violations to spot recent issues before lineups or pitch plans are finalized."
    ],
    tips: [
      "Coach and parent views are scoped so people only see the players and teams relevant to them.",
      "Most cards can be scanned without opening another page, so start here before digging deeper."
    ]
  },
  {
    id: "organizations-programs-leagues-seasons",
    title: "Organizations, Programs, Leagues, and Seasons",
    description: "These pages define the structure the rest of the app depends on.",
    roles: ["SuperAdmin", "Commish"],
    steps: [
      "Create Organizations first if you run multiple groups or clubs.",
      "Create Programs to separate baseball, softball, clinics, or other operating groups.",
      "Create Leagues and attach the right rules so rest logic works correctly.",
      "Create Seasons before building teams so rosters and schedules stay organized."
    ],
    tips: [
      "If teams or rules feel disconnected later, this is usually the first place to check.",
      "Keep naming consistent so coaches can find the right season and league quickly."
    ]
  },
  {
    id: "teams",
    title: "Teams",
    description: "The Teams area is where you create teams, assign coaches, and manage roster membership.",
    roles: ["SuperAdmin", "Commish", "Coach"],
    steps: [
      "Open Teams to create a team or edit an existing one.",
      "Assign the season, organization, program, and league so eligibility and rules stay connected.",
      "Open a team detail page to review the roster table, pitching stats, and current player list.",
      "Add or remove players directly from the team page when rosters change."
    ],
    tips: [
      "The roster table is the best place to check appearances, pitches, outs, innings, and recent use.",
      "If a player belongs to more than one team, make sure both memberships are intentional."
    ]
  },
  {
    id: "players",
    title: "Players",
    description: "The Players area is your master list for player records, team assignments, and individual history.",
    roles: ["SuperAdmin", "Commish", "Coach", "Parent"],
    steps: [
      "Use Players to add new athletes or update existing player details.",
      "Open a player profile to see their pitch history, availability context, and team relationships.",
      "Link parents to players when you want parent accounts to see the right children."
    ],
    tips: [
      "If a parent cannot see their child, check the player-parent email link first.",
      "If pitching history looks off, confirm the player record was not duplicated under a second name."
    ]
  },
  {
    id: "pitch-logs",
    title: "Pitch Logs",
    description: "Pitch Logs drive availability, rest-day math, player workload history, and violation detection.",
    roles: ["All roles with access to game activity"],
    steps: [
      "Open Pitch Logs after each game or outing.",
      "Record the player, date, team, pitch count, and outs recorded as accurately as possible.",
      "Save the log and let the app recalculate rest requirements and next available dates.",
      "Correct mistakes here instead of trying to work around them elsewhere in the app."
    ],
    tips: [
      "The rest engine is only as accurate as the pitch logs.",
      "If a pitcher looks unavailable unexpectedly, check for duplicate or incorrect outing dates here first."
    ]
  },
  {
    id: "rules",
    title: "Rules",
    description: "The Rules page controls how pitch-count and rest restrictions are enforced.",
    roles: ["SuperAdmin", "Commish"],
    steps: [
      "Create or update rules with the correct pitch thresholds and required rest days.",
      "Attach rules at the right level so the correct league or organization uses them.",
      "Review the examples carefully when rest-day counting changes are needed."
    ],
    tips: [
      "Rest days are counted as full days after the outing date.",
      "Example: pitch on 1/1 with 4 rest days means unavailable on 1/2, 1/3, 1/4, and 1/5, then available again on 1/6."
    ]
  },
  {
    id: "violations",
    title: "Violations",
    description: "The Violations page is your audit view for potential overuse or rule-breaking.",
    roles: ["SuperAdmin", "Commish", "Coach", "Parent"],
    steps: [
      "Open Violations to review recent rule issues across the players you are allowed to see.",
      "Filter by team, player, or minimum rule count to narrow the list.",
      "Open the related player or pitch log details to confirm whether the issue is real or caused by bad data."
    ],
    tips: [
      "A violation usually points to either a real usage problem or a pitch-log data problem.",
      "Resolve the bad source data before assuming the rules engine is wrong."
    ]
  },
  {
    id: "drafts",
    title: "Drafts",
    description: "Drafts help you organize player pools and distribute players more evenly across teams.",
    roles: ["SuperAdmin", "Commish"],
    steps: [
      "Create or open a draft board for the correct organization, age group, and season.",
      "Load the eligible teams so the board knows where players can go.",
      "Use auto-draft tools if you want balanced assignments, then review the results before finalizing.",
      "Finalize players only after you are confident the assignments are correct."
    ],
    tips: [
      "If no teams appear, the draft board and the real team metadata usually do not line up yet.",
      "Auto-draft is a starting point, not a substitute for a final human check."
    ]
  },
  {
    id: "users",
    title: "Users",
    description: "The Users page is where administrators invite people, edit profiles, and manage multi-role access.",
    roles: ["SuperAdmin", "Commish"],
    steps: [
      "Open Users to review the tabbed directory of coaches, parents, admins, and pending invites.",
      "Use Create User to invite a new person and assign one or more roles.",
      "Edit an existing user to update profile details, certifications, phone, ABCA ID, or role access.",
      "Use the Pending tab to watch who has not completed sign-in yet."
    ],
    tips: [
      "A person can hold multiple roles, like Parent and Coach at the same time.",
      "If Cognito account actions fail, profile data can still exist in the app, so check both the invite flow and the app profile."
    ]
  },
  {
    id: "login-profile",
    title: "Login and First-Time Setup",
    description: "New users usually land here first, so this is the best section to share with coaches and parents.",
    roles: ["All roles"],
    steps: [
      "Use the invite email to sign in through the normal login page.",
      "If the app asks you to complete your profile, fill in the required details before continuing.",
      "After login, use the sidebar to reach the pages your role allows."
    ],
    tips: [
      "If a page is missing from the sidebar, that usually means the current account does not have that role.",
      "Logging out returns you to the main entry path for the app."
    ]
  }
];

const FaqPage: React.FC = () => {
  return (
    <div className="faq-page max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-2">Help & FAQ</h1>
      <p className="text-gray-600 mb-6">
        A practical guide for the main tools in PitchTracker, with quick directions for admins, coaches, and parents.
      </p>

      <section className="bubble-card p-4 faq-hero">
        <div>
          <h2>Quick Start</h2>
          <p className="text-sm text-gray-600">
            If you are not sure where to begin, follow this order: set up your structure, build teams and players, log outings, then watch availability and violations.
          </p>
        </div>
        <div className="faq-quickstart">
          <div className="faq-step">
            <strong>1.</strong>
            <span>Set up organizations, programs, leagues, and seasons.</span>
          </div>
          <div className="faq-step">
            <strong>2.</strong>
            <span>Create teams and add players.</span>
          </div>
          <div className="faq-step">
            <strong>3.</strong>
            <span>Log every pitching outing accurately.</span>
          </div>
          <div className="faq-step">
            <strong>4.</strong>
            <span>Use the dashboard and violations pages to manage workload safely.</span>
          </div>
        </div>
      </section>

      <section className="bubble-card p-4 faq-index">
        <h2>Jump To</h2>
        <div className="faq-index__grid">
          {faqSections.map((section) => (
            <a key={section.id} href={`#${section.id}`} className="faq-index__link">
              {section.title}
            </a>
          ))}
        </div>
      </section>

      <div className="faq-section-list">
        {faqSections.map((section) => (
          <section key={section.id} id={section.id} className="bubble-card p-4 faq-section">
            <div className="faq-section__header">
              <div>
                <h2>{section.title}</h2>
                <p className="text-sm text-gray-600">{section.description}</p>
              </div>
              <div className="bubble-pill-wrap">
                {section.roles.map((role) => (
                  <span key={role} className="bubble-pill">{role}</span>
                ))}
              </div>
            </div>

            <div className="faq-columns">
              <div>
                <h3>How to use it</h3>
                <ol className="faq-list">
                  {section.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>

              {section.tips && section.tips.length > 0 ? (
                <div>
                  <h3>Helpful notes</h3>
                  <ul className="faq-list faq-list--bullets">
                    {section.tips.map((tip) => (
                      <li key={tip}>{tip}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default FaqPage;
